import React, { Fragment, memo, useEffect, useState } from "react";
import { Alert, Badge, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";

import Progress from "../../components/progress.js";
import { useAuth } from "../../components/auth/AuthProvider";
import { useLanguage } from "../../i18n/LanguageContext";
import apiUrl from "../../config/api";
import QuizScreen from "./components/QuizScreen";

import AOS from "aos";
import "../../../node_modules/aos/dist/aos";
import "../../../node_modules/aos/dist/aos.css";

import CountUp from "react-countup";
import { useSelector } from "react-redux";
import * as SettingSelector from "../../store/setting/selectors";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

const animatedStatKeys = new Set();
const SUBJECT_PLAN_COLLAPSED_LIMIT = 6;
const RECENT_QUIZZES_COLLAPSED_LIMIT = 3;

const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");

const getDashboardArchiveStorageKey = (userDetails) => (
  `quizify-dashboard-archive-id-${userDetails?.email || userDetails?.id || "local"}`
);

const getCalendarStorageKey = (userDetails) => (
  `quizify-calendar-events-${userDetails?.email || "local"}`
);

const readStoredJson = (key, fallback = []) => {
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : fallback;
  } catch (error) {
    return fallback;
  }
};

const setStoredValue = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn("Unable to save dashboard preference:", error);
  }
};

const removeStoredValue = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn("Unable to remove dashboard preference:", error);
  }
};

const capitalizeFirstLetter = (value) => {
  if (!value) return "utente";

  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatDate = (value, locale = "it-IT", emptyText = "Nessuna attività") => {
  if (!value) return emptyText;

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

const formatDuration = (seconds) => {
  const totalSeconds = Number(seconds || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  if (minutes <= 0) {
    return `${remainingSeconds}s`;
  }

  return `${minutes}m ${remainingSeconds}s`;
};

const formatNumber = (value) => new Intl.NumberFormat("it-IT").format(Number(value || 0));

const getDateKey = (dateValue) => {
  const date = typeof dateValue === "string" ? new Date(`${dateValue}T00:00:00`) : dateValue;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const todayDateKey = () => getDateKey(new Date());

const allocateTargetUnitsByWeight = (remainingByType, unitsToAllocate) => {
  const result = { closed: 0, open: 0 };
  const units = Math.max(0, Number(unitsToAllocate || 0));
  const weightedTypes = ["closed", "open"]
    .map((type) => ({
      type,
      remaining: Math.max(0, Number(remainingByType[type] || 0)),
    }))
    .filter((item) => item.remaining > 0);
  const remainingTotal = weightedTypes.reduce((sum, item) => sum + item.remaining, 0);
  const cappedUnits = Math.min(units, remainingTotal);

  if (!cappedUnits || !remainingTotal) {
    return result;
  }

  const exactShares = weightedTypes.map((item) => {
    const exact = (cappedUnits * item.remaining) / remainingTotal;

    return {
      ...item,
      exact,
      floor: Math.min(item.remaining, Math.floor(exact)),
      fraction: exact - Math.floor(exact),
    };
  });
  let assignedUnits = 0;

  exactShares.forEach((item) => {
    result[item.type] = item.floor;
    assignedUnits += item.floor;
  });

  const byRemainder = exactShares.sort((firstItem, secondItem) => (
    secondItem.fraction - firstItem.fraction
    || secondItem.remaining - firstItem.remaining
  ));

  while (assignedUnits < cappedUnits) {
    const targetItem = byRemainder.find((item) => result[item.type] < item.remaining);

    if (!targetItem) {
      break;
    }

    result[targetItem.type] += 1;
    assignedUnits += 1;
  }

  return result;
};

const splitDailyTargetByQuestionType = ({ closedRemaining, openRemaining, totalTarget }) => {
  const remaining = {
    closed: Math.max(0, Number(closedRemaining || 0)),
    open: Math.max(0, Number(openRemaining || 0)),
  };
  const remainingTotal = remaining.closed + remaining.open;
  const targetTotal = Math.min(Math.max(0, Number(totalTarget || 0)), remainingTotal);

  if (!targetTotal || !remainingTotal) {
    return { closed: 0, open: 0 };
  }

  if (!remaining.closed) {
    return { closed: 0, open: targetTotal };
  }

  if (!remaining.open) {
    return { closed: targetTotal, open: 0 };
  }

  const baseSplit = {
    closed: targetTotal >= 2 ? 1 : 0,
    open: targetTotal >= 2 ? 1 : 0,
  };
  const extraSplit = allocateTargetUnitsByWeight(
    {
      closed: remaining.closed - baseSplit.closed,
      open: remaining.open - baseSplit.open,
    },
    targetTotal - baseSplit.closed - baseSplit.open
  );

  return {
    closed: baseSplit.closed + extraSplit.closed,
    open: baseSplit.open + extraSplit.open,
  };
};

const getCalendarDuePlan = ({ events, selectedArchiveId, selectedArchiveName, subjects }) => {
  const activeEvents = events
    .filter((eventItem) => eventItem.date >= todayDateKey())
    .filter((eventItem) => eventItem.archiveId === selectedArchiveId)
    .sort((firstEvent, secondEvent) => firstEvent.date.localeCompare(secondEvent.date));

  const archiveEvent = activeEvents.find((eventItem) => eventItem.type === "archive");
  const subjectEvents = activeEvents.filter((eventItem) => eventItem.type === "subject");
  const targetEvent = archiveEvent || subjectEvents[0] || null;

  if (!targetEvent) {
    return null;
  }

  const subjectIds = archiveEvent
    ? new Set(subjects.map((subject) => subject._id))
    : new Set(subjectEvents.map((eventItem) => eventItem.subjectId));
  const subjectEventsBySubjectId = new Map(subjectEvents.map((eventItem) => [eventItem.subjectId, eventItem]));
  const plannedSubjects = subjects.filter((subject) => subjectIds.has(subject._id));
  const totalQuestions = plannedSubjects.reduce((sum, subject) => sum + Number(subject.totalQuestions || 0), 0);
  const answeredQuestions = plannedSubjects.reduce((sum, subject) => sum + Number(subject.answeredQuestions || 0), 0);
  const remainingQuestions = Math.max(0, totalQuestions - answeredQuestions);
  const completedAnswerCycles = plannedSubjects.length
    ? Math.min(...plannedSubjects.map((subject) => Number(subject.review?.completedAnswerCycles || 0)))
    : 0;
  const completedViewCycles = plannedSubjects.length
    ? Math.min(...plannedSubjects.map((subject) => Number(subject.review?.completedViewCycles || 0)))
    : 0;
  const currentReview = completedAnswerCycles + 1;
  const currentViewReview = completedViewCycles + 1;
  const currentAnswerCycleQuestions = plannedSubjects.reduce((sum, subject) => {
    const subjectCompletedCycles = Number(subject.review?.completedAnswerCycles || 0);

    if (subjectCompletedCycles > completedAnswerCycles) {
      return sum + Number(subject.totalQuestions || 0);
    }

    return sum + Number(subject.review?.currentAnswerCycleQuestions || 0);
  }, 0);
  const currentViewCycleQuestions = plannedSubjects.reduce((sum, subject) => {
    const subjectCompletedCycles = Number(subject.review?.completedViewCycles || 0);

    if (subjectCompletedCycles > completedViewCycles) {
      return sum + Number(subject.totalQuestions || 0);
    }

    return sum + Number(subject.review?.currentViewCycleQuestions || 0);
  }, 0);
  const isInitialPlanCompleted = remainingQuestions === 0 && totalQuestions > 0;
  const reviewRemainingQuestions = isInitialPlanCompleted
    ? Math.max(0, totalQuestions - currentAnswerCycleQuestions)
    : 0;
  const today = new Date(`${todayDateKey()}T00:00:00`);
  const dueDate = new Date(`${targetEvent.date}T00:00:00`);
  const daysRemaining = Math.max(1, Math.ceil((dueDate - today) / 86400000) + 1);
  const earliestCreatedAt = plannedSubjects
    .map((subject) => subject.createdAt)
    .filter(Boolean)
    .sort()[0];
  const elapsedDays = earliestCreatedAt
    ? Math.max(1, Math.ceil((today - new Date(earliestCreatedAt)) / 86400000) + 1)
    : 1;
  const currentDailyPace = answeredQuestions > 0 ? answeredQuestions / elapsedDays : 0;
  const requiredDailyPace = remainingQuestions / daysRemaining;
  const estimatedDaysToFinish = currentDailyPace > 0
    ? Math.ceil(remainingQuestions / currentDailyPace)
    : null;
  const daysDelta = estimatedDaysToFinish === null ? null : daysRemaining - estimatedDaysToFinish;
  const reviewCurrentDailyPace = currentAnswerCycleQuestions > 0 ? currentAnswerCycleQuestions / elapsedDays : 0;
  const reviewEstimatedDaysToFinish = reviewCurrentDailyPace > 0
    ? Math.ceil(reviewRemainingQuestions / reviewCurrentDailyPace)
    : null;
  const activeRemainingQuestions = isInitialPlanCompleted ? reviewRemainingQuestions : remainingQuestions;
  const activeRequiredDailyPace = activeRemainingQuestions / daysRemaining;
  const activeCurrentDailyPace = isInitialPlanCompleted ? reviewCurrentDailyPace : currentDailyPace;
  const activeAnsweredTodayQuestions = plannedSubjects.reduce((sum, subject) => (
    sum + Number(subject.review?.answeredTodayQuestions || 0)
  ), 0);
  const subjectPlans = plannedSubjects.map((subject) => {
    const subjectTargetEvent = archiveEvent || subjectEventsBySubjectId.get(subject._id) || targetEvent;
    const subjectDueDate = new Date(`${subjectTargetEvent.date}T00:00:00`);
    const subjectDaysRemaining = Math.max(1, Math.ceil((subjectDueDate - today) / 86400000) + 1);
    const subjectTotalQuestions = Number(subject.totalQuestions || 0);
    const subjectClosedQuestions = Number(subject.closedQuestions || 0);
    const subjectOpenQuestions = Number(subject.openQuestions || 0);
    const subjectAnsweredClosedQuestions = Number(subject.answeredClosedQuestions || 0);
    const subjectAnsweredOpenQuestions = Number(subject.answeredOpenQuestions || 0);
    const subjectRemainingClosedQuestions = Math.max(0, subjectClosedQuestions - subjectAnsweredClosedQuestions);
    const subjectRemainingOpenQuestions = Math.max(0, subjectOpenQuestions - subjectAnsweredOpenQuestions);
    const subjectRemainingQuestions = Math.max(0, Number(subject.totalQuestions || 0) - Number(subject.answeredQuestions || 0));
    const subjectCompletedCycles = Number(subject.review?.completedAnswerCycles || 0);
    const subjectCurrentCycleQuestions = subjectCompletedCycles > completedAnswerCycles
      ? subjectTotalQuestions
      : Number(subject.review?.currentAnswerCycleQuestions || 0);
    const subjectCurrentCycleClosedQuestions = subjectCompletedCycles > completedAnswerCycles
      ? subjectClosedQuestions
      : Number(subject.review?.currentAnswerCycleClosedQuestions || 0);
    const subjectCurrentCycleOpenQuestions = subjectCompletedCycles > completedAnswerCycles
      ? subjectOpenQuestions
      : Number(subject.review?.currentAnswerCycleOpenQuestions || 0);
    const subjectReviewRemainingQuestions = Math.max(0, subjectTotalQuestions - subjectCurrentCycleQuestions);
    const subjectReviewRemainingClosedQuestions = Math.max(0, subjectClosedQuestions - subjectCurrentCycleClosedQuestions);
    const subjectReviewRemainingOpenQuestions = Math.max(0, subjectOpenQuestions - subjectCurrentCycleOpenQuestions);
    const activeRemainingQuestions = isInitialPlanCompleted
      ? subjectReviewRemainingQuestions
      : subjectRemainingQuestions;
    const activeRemainingClosedQuestions = isInitialPlanCompleted
      ? subjectReviewRemainingClosedQuestions
      : subjectRemainingClosedQuestions;
    const activeRemainingOpenQuestions = isInitialPlanCompleted
      ? subjectReviewRemainingOpenQuestions
      : subjectRemainingOpenQuestions;
    const dailyTarget = activeRemainingQuestions / subjectDaysRemaining;
    const dailyTargetByType = splitDailyTargetByQuestionType({
      closedRemaining: activeRemainingClosedQuestions,
      openRemaining: activeRemainingOpenQuestions,
      totalTarget: Math.ceil(dailyTarget),
    });

    return {
      _id: subject._id,
      name: subject.name,
      dueDate: subjectTargetEvent.date,
      answeredQuestions: Number(subject.answeredQuestions || 0),
      totalQuestions: subjectTotalQuestions,
      closedQuestions: subjectClosedQuestions,
      openQuestions: subjectOpenQuestions,
      answeredClosedQuestions: subjectAnsweredClosedQuestions,
      answeredOpenQuestions: subjectAnsweredOpenQuestions,
      answeredTodayQuestions: Number(subject.review?.answeredTodayQuestions || 0),
      answeredTodayClosedQuestions: Number(subject.review?.answeredTodayClosedQuestions || 0),
      answeredTodayOpenQuestions: Number(subject.review?.answeredTodayOpenQuestions || 0),
      remainingQuestions: subjectRemainingQuestions,
      remainingClosedQuestions: subjectRemainingClosedQuestions,
      remainingOpenQuestions: subjectRemainingOpenQuestions,
      reviewRemainingQuestions: subjectReviewRemainingQuestions,
      reviewRemainingClosedQuestions: subjectReviewRemainingClosedQuestions,
      reviewRemainingOpenQuestions: subjectReviewRemainingOpenQuestions,
      activeRemainingQuestions,
      activeRemainingClosedQuestions,
      activeRemainingOpenQuestions,
      dailyTarget,
      dailyTargetClosedQuestions: dailyTargetByType.closed,
      dailyTargetOpenQuestions: dailyTargetByType.open,
      review: subject.review || {
        completedAnswerCycles: 0,
        completedViewCycles: 0,
        currentReview: 1,
        currentAnswerCycleQuestions: 0,
        currentAnswerCycleClosedQuestions: 0,
        currentAnswerCycleOpenQuestions: 0,
        currentViewCycleQuestions: 0,
      },
    };
  });
  const planTitle = archiveEvent || plannedSubjects.length !== 1
    ? (targetEvent.archiveName || selectedArchiveName)
    : (targetEvent.subjectName || plannedSubjects[0]?.name || selectedArchiveName);

  return {
    type: archiveEvent || plannedSubjects.length !== 1 ? "archive" : "subject",
    dueDate: targetEvent.date,
    title: planTitle,
    plannedSubjects,
    totalQuestions,
    answeredQuestions,
    remainingQuestions,
    daysRemaining,
    requiredDailyPace,
    currentDailyPace,
    estimatedDaysToFinish,
    daysDelta,
    isInitialPlanCompleted,
    reviewRemainingQuestions,
    activeRemainingQuestions,
    activeRequiredDailyPace,
    activeCurrentDailyPace,
    activeAnsweredTodayQuestions,
    reviewEstimatedDaysToFinish,
    subjectPlans,
    review: {
      completedAnswerCycles,
      completedViewCycles,
      currentReview,
      currentViewReview,
      currentAnswerCycleQuestions,
      currentViewCycleQuestions,
    },
  };
};

const AnimatedStatValue = ({ id, value }) => {
  const numericValue = Number(value || 0);
  const [hasAnimated, setHasAnimated] = useState(() => animatedStatKeys.has(id));

  if (hasAnimated) {
    return formatNumber(numericValue);
  }

  return (
    <CountUp
      end={numericValue}
      duration={1.2}
      separator="."
      onEnd={() => {
        animatedStatKeys.add(id);
        setHasAnimated(true);
      }}
    />
  );
};

const StatCard = ({ id, title, value, note, icon, variant = "primary" }) => (
  <div className="card card-slide h-100">
    <div className="card-body">
      <div className="d-flex align-items-center justify-content-between gap-3">
        <div>
          <p className="mb-2 text-muted">{title}</p>
          <h3 className="mb-1">
            <AnimatedStatValue id={id || title} value={value} />
          </h3>
          <small className="text-muted">{note}</small>
        </div>
        <div className={`rounded bg-soft-${variant} p-3 text-${variant}`}>
          {icon}
        </div>
      </div>
    </div>
  </div>
);

const EmptyState = ({ title, text, action }) => (
  <div className="py-4 text-center">
    <h6 className="mb-2">{title}</h6>
    <p className="mb-3 text-muted">{text}</p>
    {action}
  </div>
);

const getAnswerId = (question, answerIndex) => `${question._id}-answer-${answerIndex}`;

const getCorrectAnswerIds = (question) => (question.answers || [])
  .map((answer, answerIndex) => (answer.isCorrect ? getAnswerId(question, answerIndex) : null))
  .filter(Boolean);

const normalizeSelectedAnswerIds = (selectedAnswer) => {
  if (Array.isArray(selectedAnswer)) {
    return selectedAnswer;
  }

  return selectedAnswer ? [selectedAnswer] : [];
};

const isSameAnswerSet = (firstAnswers, secondAnswers) => (
  firstAnswers.length === secondAnswers.length
  && firstAnswers.every((answerId) => secondAnswers.includes(answerId))
);

const buildReviewCompletionSummary = (questions, attempt) => {
  const closedQuestions = questions.filter((question) => question.questionType === "closed");
  const openQuestions = questions.filter((question) => question.questionType === "open");
  const correctClosed = questions.filter((question) => (
    question.questionType === "closed"
    && isSameAnswerSet(normalizeSelectedAnswerIds(question.selectedAnswer), getCorrectAnswerIds(question))
  )).length;
  const wrongClosed = closedQuestions.length - correctClosed;
  const scorePercent = closedQuestions.length ? Math.round((correctClosed / closedQuestions.length) * 100) : null;

  return {
    durationSeconds: Number(attempt.durationSeconds || 0),
    closedTotal: closedQuestions.length,
    openTotal: openQuestions.length,
    correctClosed,
    wrongClosed,
    scorePercent,
    isOpenOnly: closedQuestions.length === 0 && openQuestions.length > 0,
  };
};

const Index = memo(() => {
  useSelector(SettingSelector.theme_color);
  const { isAuthLoading, userDetails } = useAuth();
  const { currentLanguage, t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [archives, setArchives] = useState([]);
  const [selectedArchiveId, setSelectedArchiveId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isArchivesLoading, setIsArchivesLoading] = useState(true);
  const [error, setError] = useState("");
  const [isStatsBeginning, setIsStatsBeginning] = useState(true);
  const [isStatsEnd, setIsStatsEnd] = useState(false);
  const [dashboardTipIndex] = useState(() => Math.floor(Math.random() * 2));
  const [quizReviewData, setQuizReviewData] = useState(null);
  const [reviewLoadingAttemptId, setReviewLoadingAttemptId] = useState(null);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const [inProgressQuizAttempt, setInProgressQuizAttempt] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isSubjectPlanExpanded, setIsSubjectPlanExpanded] = useState(false);
  const [isRecentQuizzesExpanded, setIsRecentQuizzesExpanded] = useState(false);

  const isAdmin = ["admin", "super-admin"].includes(userDetails?.role);
  const dashboardArchiveStorageKey = getDashboardArchiveStorageKey(userDetails);
  const calendarStorageKey = getCalendarStorageKey(userDetails);

  useEffect(() => {
    AOS.init({
      startEvent: "DOMContentLoaded",
      throttleDelay: 10,
      once: true,
      duration: 700,
      offset: 10,
    });
  }, []);

  useEffect(() => {
    if (!isLoading) {
      AOS.refreshHard();
    }
  }, [isLoading, stats]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    const storedEvents = readStoredJson(calendarStorageKey);
    setCalendarEvents(Array.isArray(storedEvents) ? storedEvents : []);
  }, [calendarStorageKey, isAuthLoading]);

  useEffect(() => {
    const fetchArchives = async () => {
      if (isAuthLoading) {
        return;
      }

      const token = getToken();

      if (!token) {
        setError(t.dashboard.invalidSession);
        setIsLoading(false);
        setIsArchivesLoading(false);
        return;
      }

      try {
        setIsArchivesLoading(true);
        const response = await axios.post(`${apiUrl}/getArchives`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const nextArchives = Array.isArray(response.data) ? response.data : [];
        const savedArchiveId = localStorage.getItem(dashboardArchiveStorageKey);
        const nextSelectedArchive = nextArchives.find((archive) => archive._id === savedArchiveId) || nextArchives[0];

        setArchives(nextArchives);
        setSelectedArchiveId(nextSelectedArchive?._id || "");
        if (nextSelectedArchive?._id) {
          setStoredValue(dashboardArchiveStorageKey, nextSelectedArchive._id);
        } else {
          removeStoredValue(dashboardArchiveStorageKey);
        }
        setError("");
      } catch (requestError) {
        setError(
          requestError.response?.data?.message ||
          t.dashboard.loadError
        );
      } finally {
        setIsArchivesLoading(false);
      }
    };

    fetchArchives();
  }, [dashboardArchiveStorageKey, isAuthLoading, t.dashboard.invalidSession, t.dashboard.loadError]);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      if (isAuthLoading || isArchivesLoading) {
        return;
      }

      const token = getToken();

      if (!token) {
        setError(t.dashboard.invalidSession);
        setIsLoading(false);
        return;
      }

      if (!selectedArchiveId) {
        setStats(null);
        setInProgressQuizAttempt(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const [statsResponse, inProgressResponse] = await Promise.all([
          axios.get(`${apiUrl}/dashboard/stats`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { archiveId: selectedArchiveId, _: Date.now() },
          }),
          axios.get(`${apiUrl}/quizAttempts/in-progress/latest`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { _: Date.now() },
          }),
        ]);
        setStats(statsResponse.data);
        setInProgressQuizAttempt(inProgressResponse.data.attempt || null);
        setError("");
      } catch (requestError) {
        setInProgressQuizAttempt(null);
        setError(
          requestError.response?.data?.message ||
          t.dashboard.loadError
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardStats();
  }, [dashboardRefreshKey, isAuthLoading, isArchivesLoading, selectedArchiveId, t.dashboard.invalidSession, t.dashboard.loadError]);

  const handleArchiveChange = (event) => {
    const nextArchiveId = event.target.value;

    setSelectedArchiveId(nextArchiveId);
    setIsSubjectPlanExpanded(false);
    setIsRecentQuizzesExpanded(false);
    setStoredValue(dashboardArchiveStorageKey, nextArchiveId);
  };

  const handleReviewQuizAttempt = async (attemptId) => {
    const token = getToken();

    if (!token) {
      setError(t.dashboard.invalidSession);
      return;
    }

    try {
      setReviewLoadingAttemptId(attemptId);
      const response = await axios.get(`${apiUrl}/quizAttempts/${attemptId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const questions = response.data.questions || [];
      const attempt = response.data.attempt || {};
      const restoredQuizParams = {
        timer: Number(attempt.timerMinutes || 0),
        questionType: attempt.questionType,
        ...(attempt.quizConfig || {}),
        numberOfQuestions: questions.length || Number(attempt.requestedQuestions || 0),
      };

      setQuizReviewData({
        subject: response.data.subject,
        questions,
        quizParams: restoredQuizParams,
        reviewAttempt: attempt.completedAt ? {
          startedAt: attempt.startedAt,
          selectedAnswers: questions.map((question) => question.selectedAnswer ?? null),
          completionSummary: buildReviewCompletionSummary(questions, attempt),
        } : null,
        initialAttempt: !attempt.completedAt ? {
          _id: attempt._id,
          startedAt: attempt.startedAt,
          selectedAnswers: questions.map((question) => question.selectedAnswer ?? null),
          timeRemainingSeconds: attempt.timeRemainingSeconds,
          currentQuestionIndex: attempt.currentQuestionIndex,
        } : null,
      });
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
        t.dashboard.loadError
      );
    } finally {
      setReviewLoadingAttemptId(null);
    }
  };

  if (isLoading || isArchivesLoading) {
    return (
      <div className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0 text-muted">{t.dashboard.loading}</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  const totals = stats?.totals || {};
  const subjects = stats?.subjects || [];
  const recentQuizzes = stats?.recentQuizzes || [];
  const displayedRecentQuizzes = inProgressQuizAttempt
    ? [
      inProgressQuizAttempt,
      ...recentQuizzes.filter((quizAttempt) => quizAttempt._id !== inProgressQuizAttempt._id),
    ]
    : recentQuizzes;
  const platform = stats?.platform;
  const hiddenRecentQuizzesCount = Math.max(0, displayedRecentQuizzes.length - RECENT_QUIZZES_COLLAPSED_LIMIT);
  const visibleRecentQuizzes = isRecentQuizzesExpanded
    ? displayedRecentQuizzes
    : displayedRecentQuizzes.slice(0, RECENT_QUIZZES_COLLAPSED_LIMIT);
  const selectedArchive = archives.find((archive) => archive._id === selectedArchiveId) || stats?.selectedArchive || null;
  const dateLocale = currentLanguage.code === "en" ? "en-US" : "it-IT";
  const dashboardTip = t.dashboard.tips[dashboardTipIndex] || t.dashboard.tips[0];
  const getRecentQuizArchiveName = (quizAttempt) => (
    quizAttempt.archiveName || (!quizAttempt.subjectName ? t.dashboard.noActivity : '')
  );
  const getRecentQuizSubjectName = (quizAttempt) => (
    quizAttempt.subjectName && quizAttempt.archiveName !== quizAttempt.subjectName
      ? quizAttempt.subjectName
      : ''
  );
  const averageCompletion = subjects.length
    ? Math.round(subjects.reduce((sum, subject) => sum + subject.completion, 0) / subjects.length)
    : 0;
  const calendarDuePlan = getCalendarDuePlan({
    events: calendarEvents,
    selectedArchiveId,
    selectedArchiveName: selectedArchive?.name || "",
    subjects,
  });
  const calendarReviewNumber = calendarDuePlan
    ? Math.max(1, Number(calendarDuePlan.review?.completedAnswerCycles || 0))
    : 1;
  const calendarActiveDailyTarget = calendarDuePlan
    ? Math.ceil(calendarDuePlan.activeRequiredDailyPace)
    : 0;
  const calendarReviewEstimateText = (() => {
    if (!calendarDuePlan?.isInitialPlanCompleted) {
      return "";
    }

    if (
      calendarActiveDailyTarget > 0
      && calendarDuePlan.activeAnsweredTodayQuestions >= calendarActiveDailyTarget
    ) {
      return t.dashboard.studyPlanReviewDailyReached(
        calendarDuePlan.activeAnsweredTodayQuestions,
        calendarActiveDailyTarget
      );
    }

    if (calendarDuePlan.reviewEstimatedDaysToFinish === null) {
      return t.dashboard.studyPlanReviewNoPace(calendarReviewNumber);
    }

    return t.dashboard.studyPlanReviewFinish(
      calendarReviewNumber,
      calendarDuePlan.reviewEstimatedDaysToFinish
    );
  })();
  const subjectPlanRows = calendarDuePlan?.subjectPlans || [];
  const hiddenSubjectPlanCount = Math.max(0, subjectPlanRows.length - SUBJECT_PLAN_COLLAPSED_LIMIT);
  const displayedSubjectPlanRows = isSubjectPlanExpanded
    ? subjectPlanRows
    : subjectPlanRows.slice(0, SUBJECT_PLAN_COLLAPSED_LIMIT);
  const getSubjectPlanDailyBreakdown = (subjectPlan) => {
    const parts = [];
    const closedAnsweredToday = Number(subjectPlan.answeredTodayClosedQuestions || 0);
    const closedDailyTarget = Number(subjectPlan.dailyTargetClosedQuestions || 0);
    const openAnsweredToday = Number(subjectPlan.answeredTodayOpenQuestions || 0);
    const openDailyTarget = Number(subjectPlan.dailyTargetOpenQuestions || 0);

    if (closedDailyTarget > 0 || closedAnsweredToday > 0) {
      parts.push(t.dashboard.studyPlanSubjectClosedDaily(
        closedAnsweredToday,
        closedDailyTarget
      ));
    }

    if (openDailyTarget > 0 || openAnsweredToday > 0) {
      parts.push(t.dashboard.studyPlanSubjectOpenDaily(
        openAnsweredToday,
        openDailyTarget
      ));
    }

    return parts.join(" · ");
  };
  const statCards = [
    {
      id: `${selectedArchiveId}-subjects`,
      title: t.dashboard.cards.subjects,
      value: totals.subjects,
      note: t.dashboard.cards.subjectsNote,
      variant: "primary",
      icon: (
        <svg width="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M4 3h12a3 3 0 0 1 3 3v15H7a3 3 0 0 1-3-3V3Zm3 14h10V6a1 1 0 0 0-1-1H6v13a1 1 0 0 0 1 1Z" />
        </svg>
      ),
    },
    {
      id: `${selectedArchiveId}-quizAttempts`,
      title: t.dashboard.cards.quizAttempts,
      value: totals.quizAttempts,
      note: t.dashboard.cards.quizAttemptsNote,
      variant: "info",
      icon: (
        <svg width="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7L8 5Z" />
        </svg>
      ),
    },
    {
      id: `${selectedArchiveId}-completedQuestions`,
      title: t.dashboard.cards.completedQuestions,
      value: totals.answeredQuestions,
      note: t.dashboard.cards.unansweredNote(totals.unansweredQuestions || 0),
      variant: "success",
      icon: (
        <svg width="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="m9 16.2-3.5-3.5L4 14.2l5 5 11-11-1.5-1.5L9 16.2Z" />
        </svg>
      ),
    },
    {
      id: `${selectedArchiveId}-unseenQuestions`,
      title: t.dashboard.cards.unseenQuestions,
      value: totals.unseenQuestions,
      note: t.dashboard.cards.seenNote(totals.seenQuestions || 0),
      variant: "warning",
      icon: (
        <svg width="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 5c5 0 9 5 9 7s-4 7-9 7-9-5-9-7 4-7 9-7Zm0 2c-3.67 0-6.63 3.22-7 5 .37 1.78 3.33 5 7 5s6.63-3.22 7-5c-.37-1.78-3.33-5-7-5Zm0 2.5A2.5 2.5 0 1 1 12 14a2.5 2.5 0 0 1 0-5Z" />
        </svg>
      ),
    },
    {
      id: `${selectedArchiveId}-totalQuestions`,
      title: t.dashboard.cards.totalQuestions,
      value: totals.questions,
      note: t.dashboard.cards.totalQuestionsNote,
      variant: "info",
      icon: (
        <svg width="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2a7 7 0 0 0-7 7h2a5 5 0 1 1 5 5h-1v4h2v-2.07A7.001 7.001 0 0 0 12 2Zm-1 18v2h2v-2h-2Z" />
        </svg>
      ),
    },
    {
      id: `${selectedArchiveId}-averageCompletion`,
      title: t.dashboard.cards.averageCompletion,
      value: averageCompletion,
      note: t.dashboard.cards.averageCompletionNote,
      variant: "success",
      icon: (
        <svg width="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="m9 16.2-3.5-3.5L4 14.2l5 5 11-11-1.5-1.5L9 16.2Z" />
        </svg>
      ),
    },
  ];

  if (isAdmin && platform) {
    statCards.push(
      {
        id: "platform-users",
        title: t.dashboard.cards.registeredUsers,
        value: platform.users,
        note: t.dashboard.cards.newUsersNote(platform.newUsers || 0),
        variant: "primary",
        icon: (
          <svg width="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4 0-7 2-7 4.5V20h14v-1.5C19 16 16 14 12 14Z" />
          </svg>
        ),
      },
      {
        id: "platform-subjects",
        title: t.dashboard.cards.platformSubjects,
        value: platform.subjects,
        note: t.dashboard.cards.platformSubjectsNote,
        variant: "info",
        icon: (
          <svg width="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 5h8v14H3V5Zm10 0h8v14h-8V5ZM5 7v10h4V7H5Zm10 0v10h4V7h-4Z" />
          </svg>
        ),
      },
      {
        id: "platform-questions",
        title: t.dashboard.cards.platformQuestions,
        value: platform.questions,
        note: t.dashboard.cards.platformQuestionsNote,
        variant: "success",
        icon: (
          <svg width="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 4h16v2H4V4Zm0 5h16v2H4V9Zm0 5h10v2H4v-2Zm0 5h7v2H4v-2Z" />
          </svg>
        ),
      }
    );
  }

  return (
    <Fragment>
      <Row>
        <Col lg="12">
          <div className="card" data-aos="fade-up">
            <div className="card-body dashboard-summary-card d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="dashboard-summary-copy">
                <p className="mb-1 text-muted">{t.dashboard.title}</p>
                <h3 className="mb-1">{t.dashboard.greeting(capitalizeFirstLetter(userDetails?.firstName))}</h3>
                <p className="mb-0 text-muted">{dashboardTip}</p>
              </div>
              <div className="dashboard-archive-controls">
                <div className="dashboard-archive-controls-row">
                  <Form.Group className="dashboard-archive-select-group">
                    <Form.Label className="mb-1">{t.dashboard.selectArchive}</Form.Label>
                    <Form.Select
                      className="dashboard-archive-select"
                      value={selectedArchiveId}
                      onChange={handleArchiveChange}
                      disabled={archives.length === 0}
                      title={selectedArchive?.name || t.dashboard.noArchivesAvailable}
                    >
                      {archives.length === 0 ? (
                        <option value="">{t.dashboard.noArchivesAvailable}</option>
                      ) : (
                        archives.map((archive) => (
                          <option key={archive._id} value={archive._id}>
                            {archive.name}
                          </option>
                        ))
                      )}
                    </Form.Select>
                  </Form.Group>
                  <Button as={Link} to="/archives" variant="primary" className="dashboard-manage-archive-btn">
                    {t.dashboard.manageSubjects}
                  </Button>
                </div>
                <Button as={Link} to="/calendar" variant="soft-primary" className="dashboard-calendar-link-btn">
                  {t.dashboard.openCalendar}
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {calendarDuePlan && (
        <Row>
          <Col lg="12">
            <div className="card dashboard-calendar-plan-card" data-aos="fade-up">
              <div className="card-body">
                <div className="dashboard-calendar-plan-header">
                  <div>
                    <p className="mb-1 text-muted">{t.dashboard.studyPlanLabel}</p>
                    <h5 className="mb-0">{calendarDuePlan.title}</h5>
                  </div>
                  <Badge bg={calendarDuePlan.type === "archive" ? "primary" : "success"}>
                    {calendarDuePlan.type === "archive" ? t.calendarPage.archive : t.calendarPage.subject}
                  </Badge>
                </div>
                <div className="dashboard-calendar-plan-grid">
                  <div className="dashboard-calendar-plan-metric">
                    <span>{t.dashboard.studyPlanDueDate}</span>
                    <strong>{formatDate(calendarDuePlan.dueDate, dateLocale)}</strong>
                  </div>
                  <div className="dashboard-calendar-plan-metric">
                    <span>{t.dashboard.studyPlanRemaining}</span>
                    <strong>{formatNumber(calendarDuePlan.activeRemainingQuestions)}</strong>
                  </div>
                  <div className="dashboard-calendar-plan-metric">
                    <span>{t.dashboard.studyPlanDailyTarget}</span>
                    <strong>{calendarActiveDailyTarget}</strong>
                  </div>
                  <div className="dashboard-calendar-plan-metric">
                    <span>{t.dashboard.studyPlanCurrentPace}</span>
                    <strong>{calendarDuePlan.activeCurrentDailyPace.toFixed(1)}</strong>
                  </div>
                </div>
                <div className="dashboard-calendar-plan-status">
                  {calendarDuePlan.remainingQuestions === 0 ? (
                    <Badge bg="success" className="dashboard-calendar-plan-badge">
                      {t.dashboard.studyPlanCompleted}
                    </Badge>
                  ) : calendarDuePlan.estimatedDaysToFinish === null ? (
                    <Badge bg="warning" className="dashboard-calendar-plan-badge dashboard-calendar-plan-warning">
                      {t.dashboard.studyPlanNoPace}
                    </Badge>
                  ) : calendarDuePlan.daysDelta >= 0 ? (
                    <Badge bg="success" className="dashboard-calendar-plan-badge">
                      {t.dashboard.studyPlanAhead(calendarDuePlan.daysDelta)}
                    </Badge>
                  ) : (
                    <Badge bg="danger" className="dashboard-calendar-plan-badge">
                      {t.dashboard.studyPlanLate(Math.abs(calendarDuePlan.daysDelta))}
                    </Badge>
                  )}
                  <small className="text-muted">
                    {t.dashboard.studyPlanSubjects(calendarDuePlan.plannedSubjects.length, calendarDuePlan.daysRemaining)}
                  </small>
                  {calendarDuePlan.isInitialPlanCompleted && (
                    <small className="dashboard-calendar-review-estimate">
                      {calendarReviewEstimateText}
                    </small>
                  )}
                </div>
                {calendarDuePlan.review.completedAnswerCycles > 0 && (
                  <div className="dashboard-calendar-review-progress">
                    <strong>{t.dashboard.studyPlanReviewName(calendarDuePlan.review.completedAnswerCycles)}</strong>
                    <span>{t.dashboard.studyPlanReviewAnswers}</span>
                    <b>{calendarDuePlan.review.currentAnswerCycleQuestions}/{calendarDuePlan.totalQuestions}</b>
                    <span>{t.dashboard.studyPlanReviewSeen}</span>
                    <b>{calendarDuePlan.review.currentViewCycleQuestions}/{calendarDuePlan.totalQuestions}</b>
                  </div>
                )}
                {calendarDuePlan.subjectPlans.length > 0 && (
                  <div className="dashboard-calendar-subject-plan-list">
                    <div className="dashboard-calendar-subject-plan-title">
                      {calendarDuePlan.isInitialPlanCompleted
                        ? t.dashboard.studyPlanReviewBySubject(calendarReviewNumber)
                        : t.dashboard.studyPlanBySubject}
                    </div>
                    {displayedSubjectPlanRows.map((subjectPlan) => {
                      const subjectPlanBreakdown = getSubjectPlanDailyBreakdown(subjectPlan);

                      return (
                        <div key={subjectPlan._id} className="dashboard-calendar-subject-plan-row">
                          <div className="dashboard-calendar-subject-plan-info">
                            <span title={subjectPlan.name}>{subjectPlan.name}</span>
                            {calendarDuePlan.isInitialPlanCompleted && (
                              <small>
                                {t.dashboard.studyPlanSubjectReviewProgress(
                                  subjectPlan.review?.currentAnswerCycleQuestions || 0,
                                  subjectPlan.review?.currentViewCycleQuestions || 0,
                                  subjectPlan.totalQuestions
                                )}
                              </small>
                            )}
                          </div>
                          <div className="dashboard-calendar-subject-plan-target">
                            <strong>
                              {t.dashboard.studyPlanSubjectDaily(
                                subjectPlan.answeredTodayQuestions,
                                Math.ceil(subjectPlan.dailyTarget)
                              )}
                            </strong>
                            {subjectPlanBreakdown && (
                              <small>{subjectPlanBreakdown}</small>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {hiddenSubjectPlanCount > 0 && (
                      <Button
                        type="button"
                        variant="link"
                        className="dashboard-calendar-subject-plan-toggle"
                        aria-expanded={isSubjectPlanExpanded}
                        onClick={() => setIsSubjectPlanExpanded((isExpanded) => !isExpanded)}
                      >
                        {isSubjectPlanExpanded
                          ? t.dashboard.studyPlanHideSubjects
                          : t.dashboard.studyPlanShowMoreSubjects(hiddenSubjectPlanCount)}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {archives.length === 0 ? (
        <Alert variant="primary" className="mb-4">
          {t.dashboard.noArchivesText}
        </Alert>
      ) : ""}

      <Row>
        <Col md="12" lg="12">
          <div className="overflow-hidden d-slider1 dashboard-stats-slider" data-aos="fade-up">
            <Swiper
              modules={[Navigation]}
              className="p-0 m-0 mb-2 list-inline"
              slidesPerView={4}
              spaceBetween={32}
              watchOverflow={true}
              navigation={{
                nextEl: ".dashboard-stats-next",
                prevEl: ".dashboard-stats-prev",
              }}
              onSwiper={(swiper) => {
                setIsStatsBeginning(swiper.isBeginning);
                setIsStatsEnd(swiper.isEnd || swiper.isLocked);
              }}
              onSlideChange={(swiper) => {
                setIsStatsBeginning(swiper.isBeginning);
                setIsStatsEnd(swiper.isEnd || swiper.isLocked);
              }}
              onResize={(swiper) => {
                setIsStatsBeginning(swiper.isBeginning);
                setIsStatsEnd(swiper.isEnd || swiper.isLocked);
              }}
              breakpoints={{
                320: { slidesPerView: 1 },
                550: { slidesPerView: 2 },
                991: { slidesPerView: 3 },
                1500: { slidesPerView: 4 },
                2040: { slidesPerView: 6 },
              }}
            >
              {statCards.map((card) => (
                <SwiperSlide key={card.id}>
                  <StatCard {...card} />
                </SwiperSlide>
              ))}
            </Swiper>
            <div className={`dashboard-stats-nav dashboard-stats-next ${isStatsEnd ? "d-none" : ""}`} aria-label="Next">
              <span aria-hidden="true">›</span>
            </div>
            <div className={`dashboard-stats-nav dashboard-stats-prev ${isStatsBeginning ? "d-none" : ""}`} aria-label="Previous">
              <span aria-hidden="true">‹</span>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg="8">
          <div className="card" data-aos="fade-up">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between">
              <div className="header-title">
                <h4 className="card-title mb-1">{t.dashboard.subjectsCompletion}</h4>
                <p className="mb-0 text-muted">
                  {t.dashboard.subjectsCompletionSubtitle}
                </p>
              </div>
              <Button as={Link} to="/statistics" variant="soft-primary" className="ms-auto">
                {t.nav.statistics}
              </Button>
            </div>
            <div className="card-body">
              {subjects.length === 0 ? (
                <EmptyState
                  title={t.dashboard.noSubjectsTitle}
                  text={t.dashboard.noSubjectsText}
                  action={
                    <Button as={Link} to="/archives" variant="primary">
                      {t.dashboard.createSubject}
                    </Button>
                  }
                />
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>{t.dashboard.table.subject}</th>
                        <th>{t.dashboard.table.quizAttempts}</th>
                        <th>{t.dashboard.table.questions}</th>
                        <th>{t.dashboard.table.unseen}</th>
                        <th>{t.dashboard.table.completion}</th>
                        <th>{t.dashboard.table.lastQuiz}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subjects.map((subject) => (
                        <tr key={subject._id}>
                          <td>
                            <h6 className="mb-1">{subject.name}</h6>
                            <small className="text-muted">{subject.desc || t.dashboard.noDescription}</small>
                          </td>
                          <td>
                            <Badge bg={subject.quizAttempts > 0 ? "primary" : "secondary"}>
                              {subject.quizAttempts}
                            </Badge>
                          </td>
                          <td>
                            <strong>{subject.answeredQuestions}</strong>
                            <small className="text-muted"> / {subject.totalQuestions}</small>
                          </td>
                          <td>
                            <strong>{subject.unseenQuestions}</strong>
                          </td>
                          <td style={{ minWidth: 180 }}>
                            <div className="d-flex align-items-center gap-2">
                              <Progress
                                softcolors={subject.completion === 100 ? "success" : "primary"}
                                color={subject.completion === 100 ? "success" : "primary"}
                                className="shadow-none w-100"
                                value={subject.completion}
                                minvalue={0}
                                maxvalue={100}
                                style={{ height: "6px" }}
                              />
                              <span className="text-muted">{subject.completion}%</span>
                            </div>
                          </td>
                          <td>{formatDate(subject.lastQuizAt, dateLocale, t.dashboard.noActivity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </Col>

        <Col lg="4">
          <div className="card" data-aos="fade-up">
            <div className="card-header">
              <h4 className="card-title mb-1">{t.dashboard.latestQuizzes}</h4>
              <p className="mb-0 text-muted">{t.dashboard.latestQuizzesSubtitle}</p>
            </div>
            <div className="card-body">
              {displayedRecentQuizzes.length === 0 ? (
                <EmptyState
                  title={t.dashboard.noQuizzesTitle}
                  text={t.dashboard.noQuizzesText}
                />
              ) : (
                <>
                  {visibleRecentQuizzes.map((quizAttempt) => (
                    <div key={quizAttempt._id} className="d-flex align-items-start justify-content-between gap-3 mb-3">
                      <div>
                        {getRecentQuizSubjectName(quizAttempt) ? (
                          <>
                            <small className="text-muted d-block mb-1">{getRecentQuizArchiveName(quizAttempt)}</small>
                            <h6 className="mb-1">{getRecentQuizSubjectName(quizAttempt)}</h6>
                          </>
                        ) : (
                          <h6 className="mb-1">{getRecentQuizArchiveName(quizAttempt)}</h6>
                        )}
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          {Number(quizAttempt.closedQuestions || 0) > 0 && (
                            <Badge bg="success">
                              {quizAttempt.correctClosedQuestions}/{quizAttempt.closedQuestions}
                            </Badge>
                          )}
                          {!quizAttempt.completedAt && (
                            <Badge bg="warning" text="dark">
                              {t.dashboard.inProgressQuiz}
                            </Badge>
                          )}
                          <small className="text-muted">
                            {Number(quizAttempt.requestedQuestions || quizAttempt.viewedQuestions || 0) > 0 && (
                              <>
                                {quizAttempt.answeredQuestions}/{quizAttempt.requestedQuestions || quizAttempt.viewedQuestions} {t.dashboard.answers} -{' '}
                              </>
                            )}
                            {formatDuration(quizAttempt.durationSeconds)}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex flex-column align-items-end gap-2 flex-shrink-0">
                        <Badge bg="primary">
                          {formatDate(quizAttempt.completedAt, dateLocale, t.dashboard.noActivity)}
                        </Badge>
                        <Button
                          type="button"
                          variant="soft-primary"
                          size="sm"
                          className={quizAttempt.completedAt ? "btn-icon" : "px-2 py-1 text-nowrap"}
                          aria-label={quizAttempt.completedAt ? t.dashboard.reviewQuiz : t.dashboard.continueQuiz}
                          title={quizAttempt.completedAt ? t.dashboard.reviewQuiz : t.dashboard.continueQuiz}
                          disabled={reviewLoadingAttemptId === quizAttempt._id}
                          onClick={() => handleReviewQuizAttempt(quizAttempt._id)}
                        >
                          {reviewLoadingAttemptId === quizAttempt._id ? (
                            <Spinner animation="border" size="sm" />
                          ) : !quizAttempt.completedAt ? (
                            t.dashboard.continueQuiz
                          ) : (
                            <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 5C6.5 5 3 12 3 12s3.5 7 9 7 9-7 9-7-3.5-7-9-7Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                  {hiddenRecentQuizzesCount > 0 && (
                    <Button
                      type="button"
                      variant="link"
                      className="dashboard-list-toggle"
                      aria-expanded={isRecentQuizzesExpanded}
                      onClick={() => setIsRecentQuizzesExpanded((isExpanded) => !isExpanded)}
                    >
                      {isRecentQuizzesExpanded
                        ? t.dashboard.hideRecentQuizzes
                        : t.dashboard.showMoreRecentQuizzes(hiddenRecentQuizzesCount)}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {isAdmin && platform?.recentUsers?.length > 0 && (
            <div className="card" data-aos="fade-up">
              <div className="card-header">
                <h4 className="card-title mb-1">{t.dashboard.newUsers}</h4>
                <p className="mb-0 text-muted">{t.dashboard.recentRegistrations}</p>
              </div>
              <div className="card-body">
                {platform.recentUsers.map((recentUser) => (
                  <div key={recentUser._id} className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h6 className="mb-1">
                        {recentUser.firstName} {recentUser.lastName}
                      </h6>
                      <small className="text-muted">{recentUser.email}</small>
                    </div>
                    <Badge bg="secondary">{recentUser.role}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Col>
      </Row>
      {quizReviewData && (
        <QuizScreen
          breakpoint
          subject={quizReviewData.subject}
          quizParams={quizReviewData.quizParams}
          questions={quizReviewData.questions}
          reviewAttempt={quizReviewData.reviewAttempt}
          initialAttempt={quizReviewData.initialAttempt}
          setShowQuizScreen={(isVisible) => {
            if (!isVisible) {
              setQuizReviewData(null);
              setDashboardRefreshKey((previousKey) => previousKey + 1);
            }
          }}
        />
      )}
    </Fragment>
  );
});

export default Index;
